import { SYM_LAYER_INDENTIFY } from "./enum/Symbol";
import useQueryOriginalSourceByLayer from "./layer/chain/store/useStoreOriginalSource";
import { getParentLinking, isContainParentLinking } from "./layer/utils/useMarkParentLinking";
import { getRootProps } from "./layer/chain/store/useStoreRoot";
import { useSubscribeDOMDestroyed } from "./observer/useDomObserver";
import useMap from "./utils/useMap";
import useWeakMap from "./utils/useWeakMap";

const defaultTrackingNode = Symbol('defaultNode');

/**
 * @type { UseDiscard.store}
 */
const store = useWeakMap();

window['discardStore'] = store;

const hasProps = (props) => store.has(props);
const getProps = (props) => store.get(props);
const deleteProps = (props) => store.delete(props);

const runStacks = (() => {
    const stacks = [];
    let runner;
    return (fn) => {
        stacks.push(fn)
        if (runner) return;
        runner = setTimeout(() => {
            while (stacks.length > 0) {
                stacks.shift().call(null);
            }
            runner = null;
        })
    }
})()

const addCallBack = (props, node = defaultTrackingNode, callback) => {
    if (typeof callback !== 'function') return;
    if (!hasProps(props)) return;
    if (!getProps(props).has(node)) {
        getProps(props).set(node, new Set())
    }
    getProps(props).get(node).add(callback);
}

const addNew = (props, node = defaultTrackingNode, callback) => {
    store.set(props, useMap());
    addCallBack(props, node, callback)
}

const addExisting = (props, node = defaultTrackingNode, callback) => {
    addCallBack(props, node, callback);
}

const addTriggerFromParent = (props, callback) => {
    const linkingProps = getParentLinking(props);
    if (linkingProps) {
        addCallBack(linkingProps, null, callback)
    };

    const rootProps = getRootProps(props);
    if (rootProps) {
        addCallBack(rootProps, defaultTrackingNode, callback)
    }
}

const init = (props, node = defaultTrackingNode, callback) => {
    if (hasProps(props)) {
        addExisting(props, node, callback)
    } else {
        addTriggerFromParent(props, () => {
            discardAll(props);
        });
        addNew(props, node, callback);
    }

    if (node instanceof HTMLElement) {
        useSubscribeDOMDestroyed(node, () => {
            doDiscard(props, node);
        })
    }
}

const doDiscard = (props, node = defaultTrackingNode) => {
    if (!hasProps(props)) return;
    emitDiscard(props, node);

    const p = getProps(props)
    if (p) {
        getProps(props).delete(node);
    }
    if (p && p.size === 0) {
        deleteProps(props);
    }
}

const emitDiscard = (props, node = defaultTrackingNode) => {
    // console.log("rollback - 1", props, node)
    if (!hasProps(props)) return;
    // Do Rollback
    if (!getProps(props).has(node)) return;
    const callbacks = getProps(props).get(node);
    const originalSource = useQueryOriginalSourceByLayer(props);
    const copySource = useQueryOriginalSourceByLayer(props);
    // console.log("rollback - end", props, node, callbacks.size)
    callbacks.forEach(cb => cb.call(null, props, originalSource, copySource));

    const p = getProps(props)
    if (p) {
        getProps(props).delete(node);
    }
}

const discardAll = (props) => {
    if (!hasProps(props)) return;
    const nodes = getProps(props);
    nodes.forEach((s, node) => {
        doDiscard(props, node);
    })
    store.delete(props);
    // console.log("remove discard",props)
}

/**
 * @type {UseDiscard.useDiscard}
 */
const useDiscard = (props, callback, node = defaultTrackingNode) => {
    node = node || defaultTrackingNode;
    init(props, node, callback);
    return () => store.delete(props);
}


/**
 *  @type {UseDiscard.discardProps}
 */
export const discardProps = (...props) => {
    while (props.length > 0) {
        discardAll(props.shift());
    }
}

export default useDiscard;
